import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

interface ServerErrorBinderProps {
  errors: Record<string, string>;
}

/**
 * Inline child of a react-admin SimpleForm / TabbedForm that propagates
 * server-side validation errors into react-hook-form. Inputs bound to those
 * field names render their red error state and inline message automatically.
 *
 * Usage:
 *   const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
 *   <Create mutationOptions={{ onError: err => setServerErrors(mapBackendErrors(err)) }}>
 *     <SimpleForm>
 *       <ServerErrorBinder errors={serverErrors} />
 *       <TextInput source="name" />
 *       ...
 *     </SimpleForm>
 *   </Create>
 */
export const ServerErrorBinder = ({ errors }: ServerErrorBinderProps) => {
  const { setError, clearErrors } = useFormContext();
  useEffect(() => {
    clearErrors();
    if (!errors || Object.keys(errors).length === 0) return;
    Object.entries(errors).forEach(([field, message]) => {
      setError(field as any, { type: 'server', message });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);
  return null;
};
